import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../contexts/AuthContext";
import { extractOAuthErrorCode, oauthErrorKey } from "../utils/oauthError";

function normalizeRedirect(url: string | null): string {
  if (!url) return "/home";
  if (url.startsWith("/")) return url;
  if (url.startsWith(window.location.origin)) return url.substring(window.location.origin.length);
  return "/home";
}

const YandexOAuthCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { checkAuth } = useAuth();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handleCallback = async () => {
      console.log(
        "[YandexOAuthCallback] Starting Yandex OAuth callback handler",
      );
      // ВНИМАНИЕ: window.location.href и searchParams содержат code/token,
      // поэтому в консоль попадает только факт наличия параметра.
      // Проверяем есть ли токен в URL (новый формат от бэкенда)
      const token = searchParams.get("token");
      const errorParam = searchParams.get("error");

      if (errorParam) {
        console.error(
          "[YandexOAuthCallback] Error param from Yandex:",
          errorParam,
        );
        setError(t(oauthErrorKey(errorParam)));
        return;
      }
      // Если есть токен в URL - используем его (новый формат)
      if (token) {
        // Токен — секрет: в консоль попадает только факт наличия, не значение.
        console.log("[YandexOAuthCallback] Token present in URL, saving to localStorage");
        localStorage.setItem("token", token);
        localStorage.setItem(
          "token_expiry",
          (Date.now() + 900 * 1000).toString(),
        );

        const isNew = searchParams.get("is_new_user");
        if (isNew === "true") {
          localStorage.setItem("isNewUser", "true");
        }

        console.log("[YandexOAuthCallback] Token saved to localStorage");

        // Проверяем аутентификацию
        try {
          await checkAuth();
          console.log("[YandexOAuthCallback] Auth checked");
        } catch (e) {
          console.error("[YandexOAuthCallback] Error checking auth:", e);
        }

        const redirect = normalizeRedirect(searchParams.get("redirect"));
        console.log("[YandexOAuthCallback] Navigating to", redirect);
        window.location.href = redirect;
        return;
      }

      // Старый формат: получаем код и делаем запрос к бэкенду
      const code = searchParams.get("code");
      const state = searchParams.get("state");

      if (!code) {
        console.error("[YandexOAuthCallback] No code in URL");
        setError(t(oauthErrorKey("missing_code")));
        return;
      }

      try {
        console.log("[YandexOAuthCallback] Fetching callback...");
        const response = await fetch(
          `/api/v1/auth/yandex/callback${window.location.search}`,
          {
            method: "GET",
            credentials: "include",
            headers: {
              Accept: "application/json",
            },
          },
        );

        console.log("[YandexOAuthCallback] Response status:", response.status);

        if (!response.ok) {
          // error — вложенный объект, не строка: достаём код причины из metadata.
          const errorData = await response.json().catch(() => null);
          const code = extractOAuthErrorCode(errorData);
          console.error(
            "[YandexOAuthCallback] Callback failed:",
            response.status,
            code,
          );
          throw new Error(code ?? "oauth_error_generic");
        }

        const data = await response.json();
        // Ответ содержит access_token: значение в консоль не выводим,
        // достаточно зафиксировать факт успешного обмена.

        if (data.access_token) {
          localStorage.setItem("token", data.access_token);
          localStorage.setItem(
            "token_expiry",
            (Date.now() + data.expires_in * 1000).toString(),
          );

          if (data.is_new_user) {
            localStorage.setItem("isNewUser", "true");
          }

          console.log("[YandexOAuthCallback] Token saved, redirecting...");
          const redirect = normalizeRedirect(data.redirect);
          window.location.href = redirect;
        } else {
          throw new Error("oauth_error_generic");
        }
      } catch (err: any) {
        console.error("[YandexOAuthCallback] Error:", err);
        setError(t(oauthErrorKey(err?.message)));
      }
    };

    handleCallback();
  }, [searchParams, navigate]);

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
        backgroundImage: 'linear-gradient(135deg, rgba(25, 118, 210, 0.85) 0%, rgba(33, 150, 243, 0.85) 100%), url("/background5.webp")',
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      <div style={{ textAlign: "center", color: "white" }}>
        <div
          style={{
            border: "4px solid rgba(255,255,255,0.3)",
            borderRadius: "50%",
            borderTop: "4px solid white",
            width: "40px",
            height: "40px",
            animation: "spin 1s linear infinite",
            margin: "0 auto 20px",
          }}
        />
        <div>{error || t("oauth_callback_working")}</div>
        {error && (
          <div
            style={{
              marginTop: "20px",
              padding: "20px",
              background: "rgba(255,0,0,0.2)",
              borderRadius: "8px",
            }}
          >
            <button
              onClick={() => navigate("/login")}
              style={{
                padding: "10px 20px",
                background: "white",
                color: "#667eea",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
              }}
            >
              {t("back_to_login")}
            </button>
          </div>
        )}
      </div>
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default YandexOAuthCallback;
